import Button from '@rio-cloud/rio-uikit/Button';
import Dialog from '@rio-cloud/rio-uikit/Dialog';
import { FormattedMessage } from 'react-intl';
import { BulkUpdateValidationFeedback } from '../../../sharedComponents/bulkStatusUpdate/BulkUpdateValidationFeedback';
import { BulkStatusError } from '../../../sharedComponents/bulkStatusUpdate/BulkStatusErrorMessage';
import { ServiceOrderBulkUpdatePreviewErrorCode, ServiceOrderBulkUpdatePreviewResult } from '../serviceOrder.types';
import { BulkUpdateMaxSizeErrorMessage } from '../../../sharedComponents/bulkStatusUpdate/BulkUpdateMaxSizeErrorMessage';
import { SERVICE_ORDER_MAX_SELECTION_SIZE } from './ServiceOrderBulkSelectionDropdown';
import ButtonToolbar from '@rio-cloud/rio-uikit/ButtonToolbar';
import { useState } from 'react';
import { ServiceOrderBulkUpdateFormStep } from './ServiceOrderBulkUpdateFormStep';
import { useAppDispatch } from '../../../../../configuration/setup/hooks';
import { serviceOrdersSlice } from '../../../reducers/serviceOrdersSlice';

type ServiceOrderBulkUpdateDialogProps = {
    readonly previewResult: ServiceOrderBulkUpdatePreviewResult;
    readonly onClose: () => void;
};

enum ServiceOrderBulkUpdateDialogStep {
    VALIDATION = 'validation',
    FORM = 'form',
}

const DoneButton = ({ onClick }: { onClick: () => void }) => (
    <Button iconName="rioglyph rioglyph-ok" onClick={onClick}>
        <FormattedMessage id="outboundOrderBook.common.button.done" />
    </Button>
);

const CloseButton = ({ onClick }: { onClick: () => void }) => (
    <Button onClick={onClick}>
        <FormattedMessage id="outboundOrderBook.common.button.cancel" />
    </Button>
);

export const ServiceOrderBulkUpdateDialog = ({ previewResult, onClose }: ServiceOrderBulkUpdateDialogProps) => {
    const dispatch = useAppDispatch();
    const [currentStep, setCurrentStep] = useState(ServiceOrderBulkUpdateDialogStep.VALIDATION);
    const isValidationStep = currentStep === ServiceOrderBulkUpdateDialogStep.VALIDATION;
    const isSuccess = previewResult.success;
    const validOrdersCount = isSuccess ? previewResult.serviceOrders.length : 0;
    const invalidOrders: BulkStatusError<ServiceOrderBulkUpdatePreviewErrorCode>[] = isSuccess
        ? previewResult.invalidOrders.map((error) => ({
              errorCode: error.errorCode,
              identifiers: error.orderIds,
              identifierType: 'ORDER',
          }))
        : [];
    const nextButtonDisabled = !isSuccess || validOrdersCount === 0;

    const requestError =
        !isSuccess && previewResult.errorCode === 'size-exceeded' ? (
            <BulkUpdateMaxSizeErrorMessage identifierType="ORDER" maxBulkSize={SERVICE_ORDER_MAX_SELECTION_SIZE} />
        ) : undefined;

    const handleOnClose = () => {
        if (!isValidationStep) {
            dispatch(serviceOrdersSlice.actions.clearBulkSelection());
        }
        onClose();
    };

    return (
        <Dialog show onClose={onClose} bsSize={Dialog.SIZE_LG}>
            <Dialog.Title
                title={<FormattedMessage id="outboundOrderBook.transportAssignments.bulkUpdate.dialogTitle" />}
            />
            <Dialog.Body>
                {isValidationStep ? (
                    <BulkUpdateValidationFeedback
                        validItemsCount={validOrdersCount}
                        invalidItems={invalidOrders}
                        requestError={requestError}
                    />
                ) : (
                    isSuccess && <ServiceOrderBulkUpdateFormStep validOrders={previewResult.serviceOrders} />
                )}
            </Dialog.Body>
            <Dialog.Footer>
                <ButtonToolbar>
                    {isValidationStep ? (
                        <>
                            <CloseButton onClick={handleOnClose} />
                            <Button
                                bsStyle={Button.PRIMARY}
                                onClick={() => setCurrentStep(ServiceOrderBulkUpdateDialogStep.FORM)}
                                disabled={nextButtonDisabled}
                            >
                                <FormattedMessage id="outboundOrderBook.common.button.next" />
                            </Button>
                        </>
                    ) : (
                        <DoneButton onClick={handleOnClose} />
                    )}
                </ButtonToolbar>
            </Dialog.Footer>
        </Dialog>
    );
};
